import { NavLink, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { clearAuth } from '../../api';
import CreditBadge from '../CreditBadge';
import {
  Home, Calendar, BookOpen, ShoppingCart, BarChart3, Package,
  User, LayoutGrid, Clock, Heart, Activity, Users, MessageSquare, History,
  Settings, LogOut, ChevronDown, Coins
} from 'lucide-react';

const I = { size: 20, strokeWidth: 1.8 } as const;
const Is = { size: 18, strokeWidth: 1.8 } as const;

const mainNav = [
  { to: '/', label: 'Dashboard', icon: <Home {...I} /> },
  { to: '/plan', label: 'Wochenplan', icon: <Calendar {...I} /> },
  { to: '/recipes', label: 'Rezepte', icon: <BookOpen {...I} /> },
  { to: '/shopping', label: 'Einkaufsliste', icon: <ShoppingCart {...I} /> },
  { to: '/tracking', label: 'Tracking', icon: <BarChart3 {...I} /> },
  { to: '/pantry', label: 'Vorratskammer', icon: <Package {...I} /> },
];

const settingsNav = [
  { to: '/settings/profile', label: 'Profil', icon: <User {...Is} /> },
  { to: '/settings/structure', label: 'Struktur', icon: <LayoutGrid {...Is} /> },
  { to: '/settings/products', label: 'Lebensmittel', icon: <Package {...Is} /> },
  { to: '/settings/daily-meals', label: 'Tagesmahlzeiten', icon: <Clock {...Is} /> },
  { to: '/settings/activities', label: 'Aktivitäten', icon: <Activity {...Is} /> },
  { to: '/settings/household', label: 'Haushalt', icon: <Users {...Is} /> },
  { to: '/settings/prompts', label: 'Eigene Prompts', icon: <MessageSquare {...Is} /> },
  { to: '/settings/preferences', label: 'Präferenzen', icon: <Heart {...Is} /> },
  { to: '/settings/credits', label: 'Credits', icon: <Coins {...Is} /> },
];

const bottomNav = [
  { to: '/review', label: 'Wochen-Review', icon: <BarChart3 {...Is} /> },
  { to: '/changelog', label: 'Changelog', icon: <History {...Is} /> },
];

export default function Sidebar() {
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(
    location.pathname.startsWith('/settings') || location.pathname.startsWith('/pantry')
  );

  return (
    <aside className="hidden md:flex md:flex-col w-64 bg-white border-r border-warm-200 h-screen sticky top-0">
      <div className="flex items-center gap-3 px-6 py-5 border-b border-warm-200">
        <img src="/nico-transparent.svg" alt="Nico" className="w-9 h-9" />
        <span className="text-xl font-display font-semibold text-teal-800 tracking-wide">plate</span>
      </div>

      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {mainNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-teal-50 text-teal-500'
                  : 'text-warm-400 hover:bg-warm-50 hover:text-warm-800'
              }`
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}

        <div className="pt-2">
          <button
            onClick={() => setSettingsOpen(!settingsOpen)}
            className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              location.pathname.startsWith('/settings')
                ? 'bg-teal-50 text-teal-500'
                : 'text-warm-400 hover:bg-warm-50 hover:text-warm-800'
            }`}
          >
            <div className="flex items-center gap-3">
              <Settings {...I} />
              Einstellungen
            </div>
            <ChevronDown size={16} strokeWidth={2} className={`transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />
          </button>

          {settingsOpen && (
            <div className="ml-4 mt-1 space-y-0.5">
              {settingsNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive
                        ? 'text-teal-500 bg-teal-50/50 font-medium'
                        : 'text-warm-400 hover:text-warm-700 hover:bg-warm-50'
                    }`
                  }
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </nav>

      <div className="px-3 py-3 border-t border-warm-200 space-y-0.5">
        <CreditBadge />
        {bottomNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive
                  ? 'text-teal-500 bg-teal-50/50 font-medium'
                  : 'text-warm-400 hover:text-warm-700 hover:bg-warm-50'
              }`
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}
        <button
          onClick={() => { clearAuth(); window.location.replace('/login'); }}
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors text-coral-400 hover:text-coral-600 hover:bg-coral-50 w-full"
        >
          <LogOut {...Is} />
          Abmelden
        </button>
      </div>
    </aside>
  );
}
